import { CommerceEmptyState } from "@/components/commerce/empty-state";
import { StatusPill } from "@/components/commerce/grid-cells";
import { formatTimestamp } from "@/components/commerce/shared";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
	type StatusBarItem,
	useWorkspacePage,
} from "@/components/workspace";
import { PromotionService } from "@/gen/omnios/v1/promotion_connect";
import type { PromotionRule } from "@/gen/omnios/v1/promotion_pb";
import { useGridSelection } from "@/hooks/use-grid-selection";
import { AG_GRID_LOCALE_RU } from "@/lib/ag-grid-locale-ru";
import { transport } from "@/lib/grpc";
import { useGridLayout } from "@/lib/use-grid-layout";

import type { ColDef } from "@ag-grid-community/core";
import { AgGridReact } from "@ag-grid-community/react";
import "@ag-grid-community/styles/ag-grid.css";
import "@ag-grid-community/styles/ag-theme-quartz.css";

import { useServerGrid } from "@/lib/use-server-grid";
import { createClient } from "@connectrpc/connect";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Loader2, Pencil, Plus, Search, Trash2 } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/commerce/promotions")({
	component: PromotionsPage,
});

const promoClient = createClient(PromotionService, transport);

interface ConditionDraft {
	field: string;
	operator: string;
	value: string;
}

interface ActionDraft {
	type: string;
	appliesTo: string;
	value: number;
	productId: string;
}

interface RuleForm {
	name: string;
	description: string;
	priority: number;
	startsAt: string;
	endsAt: string;
	conditions: ConditionDraft[];
	actions: ActionDraft[];
}

const emptyForm: RuleForm = {
	name: "",
	description: "",
	priority: 100,
	startsAt: "",
	endsAt: "",
	conditions: [],
	actions: [],
};

function PromotionsPage() {
	const { t } = useTranslation("commerce");
	const { onGridReady, onStateChanged } = useGridLayout("commerce-promotions");

	const [showDialog, setShowDialog] = useState(false);
	const [editId, setEditId] = useState<string | null>(null);
	const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
	const [form, setForm] = useState<RuleForm>({ ...emptyForm });

	const { gridRef, gridProps, totalRows, refreshGrid } = useServerGrid<PromotionRule>({
		fetchRows: async ({ pageSize, pageToken }) => {
			const resp = await promoClient.listPromotionRules({
				page: { pageSize, pageToken },
			});
			return {
				rows: resp.rules,
				nextPageToken: resp.page?.nextPageToken,
				totalCount: resp.page?.totalCount ?? 0,
			};
		},
		colToField: {
			isActive: "is_active",
		},
	});

	const dateToTs = (s: string) =>
		s
			? {
					seconds: BigInt(Math.floor(new Date(s).getTime() / 1000)),
					nanos: 0,
				}
			: undefined;

	const createMut = useMutation({
		mutationFn: () =>
			promoClient.createPromotionRule({
				name: form.name,
				description: form.description,
				priority: form.priority,
				startsAt: dateToTs(form.startsAt),
				endsAt: dateToTs(form.endsAt),
				conditions: form.conditions.map((c) => ({
					field: c.field,
					operator: c.operator,
					value: c.value,
				})),
				actions: form.actions.map((a) => ({
					type: a.type,
					appliesTo: a.appliesTo,
					value: a.value,
					productId: a.productId,
				})),
			}),
		onSuccess: () => {
			refreshGrid();
			closeDialog();
		},
	});

	const updateMut = useMutation({
		mutationFn: () =>
			promoClient.updatePromotionRule({
				id: editId!,
				name: form.name,
				description: form.description,
				priority: form.priority,
				startsAt: dateToTs(form.startsAt),
				endsAt: dateToTs(form.endsAt),
				conditions: form.conditions.map((c) => ({
					field: c.field,
					operator: c.operator,
					value: c.value,
				})),
				actions: form.actions.map((a) => ({
					type: a.type,
					appliesTo: a.appliesTo,
					value: a.value,
					productId: a.productId,
				})),
			}),
		onSuccess: () => {
			refreshGrid();
			closeDialog();
		},
	});

	const deleteMut = useMutation({
		mutationFn: (id: string) => promoClient.deletePromotionRule({ id }),
		onSuccess: () => {
			refreshGrid();
			setDeleteTarget(null);
		},
	});

	function closeDialog() {
		setShowDialog(false);
		setEditId(null);
		setForm({ ...emptyForm });
	}

	function openEdit(rule: PromotionRule) {
		setEditId(rule.id);
		setForm({
			name: rule.name,
			description: rule.description,
			priority: rule.priority,
			startsAt: rule.startsAt
				? new Date(Number(rule.startsAt.seconds) * 1000)
						.toISOString()
						.split("T")[0]
				: "",
			endsAt: rule.endsAt
				? new Date(Number(rule.endsAt.seconds) * 1000)
						.toISOString()
						.split("T")[0]
				: "",
			conditions: rule.conditions.map((c) => ({
				field: c.field,
				operator: c.operator,
				value: c.value,
			})),
			actions: rule.actions.map((a) => ({
				type: a.type,
				appliesTo: a.appliesTo,
				value: a.value,
				productId: a.productId,
			})),
		});
		setShowDialog(true);
	}

	const { selectedRows, hasSelection, onSelectionChanged } =
		useGridSelection<PromotionRule>({
			onDelete: (rows) => {
				for (const r of rows) {
					deleteMut.mutate(r.id);
				}
			},
			isPending: deleteMut.isPending,
			entityLabel: t("promo_count_label", {
				defaultValue: "акций",
			}),
		});

	// ── Column definitions ──
	const columnDefs = useMemo<ColDef<PromotionRule>[]>(
		() => [
			{
				headerCheckboxSelection: true,
				checkboxSelection: true,
				width: 36,
				maxWidth: 36,
				pinned: "left",
				suppressHeaderMenuButton: true,
				resizable: false,
			},
			{
				field: "name",
				headerName: t("promo_name"),
				flex: 1,
				minWidth: 150,
				cellRenderer: (p: { data: PromotionRule | undefined }) => {
					if (!p.data) return null;
					return (
						<div className="flex flex-col justify-center leading-tight">
							<span className="truncate text-[13px]">{p.data.name}</span>
							{p.data.description && (
								<span className="truncate text-[11px] text-muted-foreground">
									{p.data.description}
								</span>
							)}
						</div>
					);
				},
			},
			{
				field: "priority",
				headerName: t("promo_priority"),
				width: 80,
				type: "rightAligned",
				cellClass: "tabular-nums",
				filter: "agNumberColumnFilter",
			},
			{
				field: "isActive",
				headerName: t("promo_status", { defaultValue: "Статус" }),
				width: 100,
				filter: false,
				cellRenderer: (p: { data: PromotionRule | undefined }) => {
					if (!p.data) return null;
					return p.data.isActive ? (
						<StatusPill label={t("promo_active")} color="hsl(var(--chart-2))" />
					) : (
						<StatusPill
							label={t("promo_inactive")}
							color="hsl(var(--muted-foreground))"
						/>
					);
				},
			},
			{
				headerName: t("promo_conditions"),
				width: 100,
				type: "rightAligned",
				cellClass: "tabular-nums",
				filter: false,
				valueGetter: (p) => p.data?.conditions?.length ?? 0,
			},
			{
				headerName: t("promo_actions"),
				width: 100,
				type: "rightAligned",
				cellClass: "tabular-nums",
				filter: false,
				valueGetter: (p) => p.data?.actions?.length ?? 0,
			},
			{
				headerName: t("promo_date_range", { defaultValue: "Период" }),
				width: 180,
				filter: false,
				valueGetter: (p) => {
					if (!p.data) return "";
					return `${formatTimestamp(p.data.startsAt)} — ${formatTimestamp(p.data.endsAt)}`;
				},
			},
			{
				headerName: t("promo_grid_actions", { defaultValue: "Действия" }),
				width: 80,
				pinned: "right",
				suppressHeaderMenuButton: true,
				sortable: false,
				filter: false,
				resizable: false,
				cellRenderer: (p: { data: PromotionRule | undefined }) => {
					if (!p.data) return null;
					const rule = p.data;
					return (
						<div className="flex items-center gap-1">
							<button
								type="button"
								className="inline-flex h-6 w-6 items-center justify-center rounded hover:bg-accent"
								onClick={(e) => {
									e.stopPropagation();
									openEdit(rule);
								}}
							>
								<Pencil className="h-3.5 w-3.5" />
							</button>
							<button
								type="button"
								className="inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-accent hover:text-destructive"
								onClick={(e) => {
									e.stopPropagation();
									setDeleteTarget(rule.id);
								}}
							>
								<Trash2 className="h-3.5 w-3.5" />
							</button>
						</div>
					);
				},
			},
		],
		[t],
	);

	const defaultColDef = useMemo<ColDef>(
		() => ({
			sortable: true,
			resizable: true,
			suppressHeaderMenuButton: false,
			filter: true,
			filterParams: { buttons: ["apply", "reset"], closeOnApply: true },
		}),
		[],
	);

	const onRowClicked = useCallback(
		(e: { data: PromotionRule | undefined }) => {
			if (e.data) openEdit(e.data);
		},
		[],
	);

	const statusBarItems = useMemo<StatusBarItem[]>(
		() => [
			{
				id: "count",
				content: hasSelection
					? `${selectedRows.length} ${t("common:selected", { defaultValue: "выбрано" })}`
					: `${totalRows} ${t("promo_count_label", { defaultValue: "акций" })}`,
				align: "left" as const,
			},
		],
		[hasSelection, selectedRows.length, totalRows, t],
	);

	useWorkspacePage({ statusBar: statusBarItems });

	return (
		<>
			<div className="flex items-center gap-2 px-4 py-2 shrink-0">
				<div className="relative flex-1">
					<Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
					<Input
						placeholder={t("search_placeholder", { defaultValue: "Поиск..." })}
						onChange={(e) =>
							gridRef.current?.api?.setGridOption("quickFilterText", e.target.value)
						}
						className="pl-9 h-9"
					/>
				</div>
				<Button
					variant="outline"
					size="sm"
					className="h-9"
					onClick={() => refreshGrid()}
				>
					{t("common:refresh", { defaultValue: "Обновить" })}
				</Button>
				<Button
					variant="outline"
					size="sm"
					className="h-9"
					onClick={() =>
						gridRef.current?.api?.exportDataAsCsv({
							fileName: "promotions.csv",
						})
					}
				>
					{t("common:export", { defaultValue: "Экспорт" })}
				</Button>
				<Button
					size="sm"
					className="h-9"
					onClick={() => {
						setForm({ ...emptyForm });
						setEditId(null);
						setShowDialog(true);
					}}
				>
					<Plus className="h-4 w-4 mr-1" />
					{t("common:create")}
				</Button>
			</div>
			<div className="flex-1 ag-theme-quartz h-full" style={{ width: "100%" }}>
				<AgGridReact<PromotionRule>
					ref={gridRef}
					{...gridProps}
					columnDefs={columnDefs}
					defaultColDef={defaultColDef}
					localeText={AG_GRID_LOCALE_RU}
					rowSelection="multiple"
					onSelectionChanged={onSelectionChanged}
					onRowClicked={onRowClicked}
					onGridReady={onGridReady}
					onSortChanged={() => { onStateChanged(); refreshGrid(); }}
					onFilterChanged={() => { onStateChanged(); refreshGrid(); }}
					onColumnMoved={onStateChanged}
					onColumnResized={onStateChanged}
					onColumnVisible={onStateChanged}
					suppressRowClickSelection
					rowClass="cursor-pointer"
					animateRows={false}
					rowHeight={28}
					headerHeight={32}
					getRowId={(p) => p.data?.id}
					noRowsOverlayComponent={() => (
						<CommerceEmptyState
							title={t("promo_empty_title", {
								defaultValue: "Акций пока нет",
							})}
							description={t("promo_empty_description", {
								defaultValue: "Создайте первую акцию",
							})}
						/>
					)}
				/>
			</div>

			{/* Create / Edit dialog */}
			<Dialog open={showDialog} onOpenChange={(o) => !o && closeDialog()}>
				<DialogContent className="max-h-[85vh] max-w-xl overflow-y-auto">
					<DialogHeader>
						<DialogTitle>
							{editId ? t("promo_edit") : t("promo_create")}
						</DialogTitle>
					</DialogHeader>
					<div className="space-y-4 py-4">
						<div className="space-y-2">
							<Label>{t("promo_name")}</Label>
							<Input
								value={form.name}
								onChange={(e) => setForm({ ...form, name: e.target.value })}
								placeholder={t("promo_name_placeholder")}
							/>
						</div>
						<div className="space-y-2">
							<Label>{t("promo_description")}</Label>
							<Textarea
								value={form.description}
								onChange={(e) =>
									setForm({ ...form, description: e.target.value })
								}
							/>
						</div>
						<div className="grid grid-cols-3 gap-3">
							<div className="space-y-2">
								<Label>{t("promo_priority")}</Label>
								<Input
									type="number"
									value={form.priority}
									onChange={(e) =>
										setForm({ ...form, priority: Number(e.target.value) })
									}
								/>
							</div>
							<div className="space-y-2">
								<Label>{t("promo_starts_at")}</Label>
								<Input
									type="date"
									value={form.startsAt}
									onChange={(e) =>
										setForm({ ...form, startsAt: e.target.value })
									}
								/>
							</div>
							<div className="space-y-2">
								<Label>{t("promo_ends_at")}</Label>
								<Input
									type="date"
									value={form.endsAt}
									onChange={(e) => setForm({ ...form, endsAt: e.target.value })}
								/>
							</div>
						</div>

						{/* Conditions */}
						<div className="space-y-2">
							<div className="flex items-center justify-between">
								<Label>{t("promo_conditions")}</Label>
								<Button
									variant="outline"
									size="sm"
									className="h-7 text-xs"
									onClick={() =>
										setForm({
											...form,
											conditions: [
												...form.conditions,
												{ field: "order_total", operator: "gte", value: "" },
											],
										})
									}
								>
									<Plus className="mr-1 h-3 w-3" />
									{t("common:add")}
								</Button>
							</div>
							{form.conditions.map((cond, i) => (
								<div key={i} className="flex items-center gap-2">
									<Select
										value={cond.field}
										onValueChange={(v) => {
											const c = [...form.conditions];
											c[i] = { ...c[i], field: v };
											setForm({ ...form, conditions: c });
										}}
									>
										<SelectTrigger className="w-36">
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="order_total">
												{t("cond_order_total")}
											</SelectItem>
											<SelectItem value="item_count">
												{t("cond_item_count")}
											</SelectItem>
											<SelectItem value="customer_segment">
												{t("cond_customer_segment")}
											</SelectItem>
											<SelectItem value="product_category">
												{t("cond_product_category")}
											</SelectItem>
										</SelectContent>
									</Select>
									<Select
										value={cond.operator}
										onValueChange={(v) => {
											const c = [...form.conditions];
											c[i] = { ...c[i], operator: v };
											setForm({ ...form, conditions: c });
										}}
									>
										<SelectTrigger className="w-24">
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="gte">&ge;</SelectItem>
											<SelectItem value="lte">&le;</SelectItem>
											<SelectItem value="eq">=</SelectItem>
											<SelectItem value="in">{t("cond_in")}</SelectItem>
											<SelectItem value="not_in">{t("cond_not_in")}</SelectItem>
										</SelectContent>
									</Select>
									<Input
										className="flex-1"
										value={cond.value}
										onChange={(e) => {
											const c = [...form.conditions];
											c[i] = { ...c[i], value: e.target.value };
											setForm({ ...form, conditions: c });
										}}
										placeholder={t("promo_value")}
									/>
									<Button
										variant="ghost"
										size="sm"
										className="h-7 w-7 shrink-0 p-0 text-muted-foreground hover:text-destructive"
										onClick={() => {
											const c = form.conditions.filter((_, idx) => idx !== i);
											setForm({ ...form, conditions: c });
										}}
									>
										<Trash2 className="h-3.5 w-3.5" />
									</Button>
								</div>
							))}
						</div>

						{/* Actions */}
						<div className="space-y-2">
							<div className="flex items-center justify-between">
								<Label>{t("promo_actions")}</Label>
								<Button
									variant="outline"
									size="sm"
									className="h-7 text-xs"
									onClick={() =>
										setForm({
											...form,
											actions: [
												...form.actions,
												{
													type: "discount_percentage",
													appliesTo: "order",
													value: 0,
													productId: "",
												},
											],
										})
									}
								>
									<Plus className="mr-1 h-3 w-3" />
									{t("common:add")}
								</Button>
							</div>
							{form.actions.map((act, i) => (
								<div key={i} className="flex items-center gap-2">
									<Select
										value={act.type}
										onValueChange={(v) => {
											const a = [...form.actions];
											a[i] = { ...a[i], type: v };
											setForm({ ...form, actions: a });
										}}
									>
										<SelectTrigger className="w-40">
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="discount_percentage">
												{t("act_discount_pct")}
											</SelectItem>
											<SelectItem value="discount_fixed">
												{t("act_discount_fixed")}
											</SelectItem>
											<SelectItem value="free_shipping">
												{t("act_free_shipping")}
											</SelectItem>
											<SelectItem value="free_item">
												{t("act_free_item")}
											</SelectItem>
										</SelectContent>
									</Select>
									<Select
										value={act.appliesTo}
										onValueChange={(v) => {
											const a = [...form.actions];
											a[i] = { ...a[i], appliesTo: v };
											setForm({ ...form, actions: a });
										}}
									>
										<SelectTrigger className="w-28">
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="order">
												{t("act_target_order")}
											</SelectItem>
											<SelectItem value="line_item">
												{t("act_target_line")}
											</SelectItem>
											<SelectItem value="shipping">
												{t("act_target_shipping")}
											</SelectItem>
										</SelectContent>
									</Select>
									<Input
										type="number"
										className="w-24"
										value={act.value}
										onChange={(e) => {
											const a = [...form.actions];
											a[i] = { ...a[i], value: Number(e.target.value) };
											setForm({ ...form, actions: a });
										}}
										placeholder={t("promo_value")}
									/>
									<Button
										variant="ghost"
										size="sm"
										className="h-7 w-7 shrink-0 p-0 text-muted-foreground hover:text-destructive"
										onClick={() => {
											const a = form.actions.filter((_, idx) => idx !== i);
											setForm({ ...form, actions: a });
										}}
									>
										<Trash2 className="h-3.5 w-3.5" />
									</Button>
								</div>
							))}
						</div>
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={closeDialog}>
							{t("common:cancel")}
						</Button>
						<Button
							onClick={() => (editId ? updateMut.mutate() : createMut.mutate())}
							disabled={
								!form.name || createMut.isPending || updateMut.isPending
							}
						>
							{(createMut.isPending || updateMut.isPending) && (
								<Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
							)}
							{editId ? t("common:save") : t("common:create")}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Delete confirmation */}
			<AlertDialog
				open={!!deleteTarget}
				onOpenChange={(o) => !o && setDeleteTarget(null)}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>{t("promo_delete")}</AlertDialogTitle>
						<AlertDialogDescription>
							{t("promo_delete_hint")}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>{t("common:cancel")}</AlertDialogCancel>
						<AlertDialogAction
							onClick={() => deleteTarget && deleteMut.mutate(deleteTarget)}
							className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
						>
							{t("common:delete")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	);
}
